import React from 'react';
import { Select, SelectProps, useColorModeValue } from '@cardboard-ui/react';
import {
  RSVPButton_updateEventInvitation_Mutation,
  RsvpState,
  RsvpStateInput,
} from './__generated__/RSVPButton_updateEventInvitation_Mutation.graphql';
import { graphql, useMutation } from 'utils/graphClient';
import { t } from '@lingui/macro';

const updateEventInvitationMutation = graphql`
  mutation RSVPButton_updateEventInvitation_Mutation(
    $id: ID!
    $rsvpState: RsvpStateInput!
  ) {
    updateEventInvitation(input: { id: $id, rsvpState: $rsvpState }) {
      invitationUpdated {
        id
        rsvp
        event {
          ...InviteeOverview
        }
      }
    }
  }
`;

const buttonColor = {
  NOT_RESPONDED: ['blue.500', 'blue.600'],
  MAYBE: ['blue.500', 'blue.600'],
  ATTENDING: ['green.500', 'green.600'],
  DECLINED: ['gray.300', 'gray.500'],
  '%future added value': ['blue.500', 'blue.600'],
};

export interface RSVPButtonProps extends Pick<SelectProps, 'size' | 'hidden'> {
  initialStatus?: RsvpState;
  invitationId: string;
}

export const RSVPButton = ({
  initialStatus,
  invitationId,
  ...props
}: RSVPButtonProps) => {
  const notRespondedPlaceholder =
    initialStatus === 'NOT_RESPONDED' ? { placeholder: t`RSVP` } : {};

  const [bgColor, bgColorOnHover] = initialStatus
    ? buttonColor[initialStatus]
    : buttonColor['NOT_RESPONDED'];

  const [updateEventRsvp, isUpdatingEventRsvp] =
    useMutation<RSVPButton_updateEventInvitation_Mutation>(
      updateEventInvitationMutation,
    );

  // As a Mac-first development team, we are a bit biased.
  // Browsers on Mac show a native select-picker, which cannot be restyled.
  // But on Windows, this is different. The option element will inherit styles from the select element
  // Which caused the text to be unreadable.
  // To solve this, we need to explicitely override the style
  const optionStyles = useColorModeValue(
    { color: 'gray.600', bg: 'gray.100', pl: 2 },
    { color: 'gray.100', bg: 'gray.600', pl: 2 },
  );

  return (
    <Select
      {...notRespondedPlaceholder}
      isDisabled={isUpdatingEventRsvp}
      size="sm"
      {...props}
      value={initialStatus}
      variant="filled"
      bg={bgColor}
      color="white"
      width="max-content"
      borderRadius={6}
      fontWeight="600"
      _focus={{ bg: bgColorOnHover }}
      _hover={{ bg: bgColorOnHover }}
      onClick={(e) => e.stopPropagation()}
      sx={{ 'option, option:hover, option:focus': optionStyles }}
      onChange={({ target }) => {
        const newRSVPState = target.value as RsvpState;
        updateEventRsvp({
          variables: {
            id: invitationId,
            rsvpState: target.value as RsvpStateInput,
          },
          optimisticResponse: {
            updateEventInvitation: {
              invitationUpdated: {
                id: invitationId,
                rsvp: newRSVPState,
              },
            },
          },
        });
      }}
    >
      <option value="ATTENDING">{t`Attending`}</option>
      <option value="MAYBE">{t`Maybe`}</option>
      <option value="DECLINED">{t`Not attending`}</option>
    </Select>
  );
};
